/**
 * Gift with purchase (GWP) — pure vanilla JS.
 *
 * Drives the side-cart free-gift banner ([data-cart-gwp-banner]):
 *   - Shows the first applicable config when the cart qualifies
 *   - Hides when the gift is already in the cart
 *   - Adds the gift via /cart/add.js (no page navigation)
 *   - Re-checks qualification on mini-cart and cart page changes
 */
(function () {
  "use strict";

  var GWP_MAX_LINE_QUANTITY = 1;
  var SYNC_DEBOUNCE_MS = 120;
  var BANNER_SELECTOR = "[data-cart-gwp-banner]";

  function getGwpConfigs() {
    var raw = window.gwp_configs;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter(function (cfg) {
      return cfg && cfg.variant_id != null && cfg.variant_id !== "";
    });
  }

  function gwpConfigKey(cfg) {
    var key = cfg.id != null ? cfg.id : cfg.handle != null ? cfg.handle : cfg.variant_id;
    return String(key == null ? "" : key);
  }

  function isFreeProductProperty(value) {
    return value === 1 || value === "1" || value === true;
  }

  function isTruthyFlag(value) {
    return value === true || value === 1 || value === "1" || value === "true";
  }

  /** Whether a config should be added to the cart automatically (no ATC button). */
  function isAutoAdd(cfg) {
    return isTruthyFlag(cfg && cfg.auto_add_to_cart);
  }

  /** Whether this config's cart-drawer banner should stay hidden. */
  function isHiddenOnCartDrawer(cfg) {
    return isTruthyFlag(cfg && cfg.hide_on_cart_drawer);
  }

  function idSet(ids) {
    var set = {};
    (ids || []).forEach(function (id) {
      set[String(id)] = true;
    });
    return set;
  }

  function collectionProductIdSet(cfg) {
    return idSet(cfg.collection_product_ids);
  }

  /** Product IDs from the entry's product_trigger field (OR'd with the collection). */
  function productTriggerIdSet(cfg) {
    return idSet(cfg.product_trigger_ids);
  }

  function parseTrigger(cfg) {
    var trigger = parseInt(String(cfg.quantity_trigger == null ? "1" : cfg.quantity_trigger), 10);
    if (!isFinite(trigger) || trigger < 1) {
      return 1;
    }
    return trigger;
  }

  /**
   * Cart spend threshold in cents. Prefers cart_price_trigger_cents from Liquid,
   * falling back to cart_price_trigger (major currency units). 0 = no price gate.
   */
  function parsePriceTriggerCents(cfg) {
    var centsRaw = parseFloat(String(cfg.cart_price_trigger_cents == null ? "" : cfg.cart_price_trigger_cents));
    if (isFinite(centsRaw) && centsRaw > 0) {
      return Math.round(centsRaw);
    }

    var raw = parseFloat(String(cfg.cart_price_trigger == null ? "0" : cfg.cart_price_trigger));
    if (!isFinite(raw) || raw <= 0) {
      return 0;
    }
    // Large whole numbers from a money/number field are often already cents.
    if (raw === Math.round(raw) && raw >= 1000) {
      return raw;
    }
    return Math.round(raw * 100);
  }

  function lineSpendCents(item) {
    if (!item) {
      return 0;
    }
    if (typeof item.final_line_price === "number") {
      return item.final_line_price;
    }
    if (typeof item.line_price === "number") {
      return item.line_price;
    }
    return 0;
  }

  function isGwpGiftVariantLine(item) {
    if (!item || item.variant_id == null) {
      return false;
    }
    return getGwpConfigs().some(function (cfg) {
      return String(cfg.variant_id) === String(item.variant_id);
    });
  }

  /**
   * Cart spend used for the price gate — excludes free-gift lines so a gift
   * already in the cart cannot keep the total above the threshold forever.
   * Uses final_line_price so line/cart discounts are respected.
   */
  function cartSpendExcludingGiftsCents(cartItems) {
    return cartItems.reduce(function (sum, item) {
      if (!item) {
        return sum;
      }
      var props = item.properties || {};
      if (isFreeProductProperty(props["_free-product"])) {
        return sum;
      }
      if (isGwpGiftVariantLine(item) && lineSpendCents(item) === 0) {
        return sum;
      }
      return sum + lineSpendCents(item);
    }, 0);
  }

  /**
   * Whether the cart qualifies for a given gift config.
   * The price gate (cart_price_trigger) is authoritative and evaluated
   * independently of the collection set, so it also governs auto-removal when
   * the cart total drops below the threshold. The trigger/quantity gate is
   * only enforced when the trigger collection and/or product_trigger resolved
   * to a product set; collection_trigger and product_trigger are OR'd — the
   * gift fires when either one reaches quantity_trigger on its own.
   */
  function qualifiesForGift(cfg, cartItems, cartSpendCents) {
    var quantityTrigger = parseTrigger(cfg);
    var priceTriggerCents = parsePriceTriggerCents(cfg);
    var collectionIds = collectionProductIdSet(cfg);
    var productTriggerIds = productTriggerIdSet(cfg);
    var hasCollection = Object.keys(collectionIds).length > 0;
    var hasProductTrigger = Object.keys(productTriggerIds).length > 0;
    var hasPriceTrigger = priceTriggerCents > 0;

    // Needs at least one usable trigger.
    if (!hasCollection && !hasProductTrigger && !hasPriceTrigger) {
      return false;
    }

    // Never qualify a cart with no paid (non-gift) spend.
    if (cartSpendCents <= 0) {
      return false;
    }

    // Price gate — cart total must reach the trigger (equal or greater).
    if (hasPriceTrigger && cartSpendCents < priceTriggerCents) {
      return false;
    }

    // Collection OR product trigger / quantity gate — only when a product set resolved.
    if (hasCollection || hasProductTrigger) {
      var giftVid = String(cfg.variant_id);
      var collectionOk =
        hasCollection && qualifyingQuantity(cartItems, giftVid, collectionIds) >= quantityTrigger;
      var productOk =
        hasProductTrigger && qualifyingQuantity(cartItems, giftVid, productTriggerIds) >= quantityTrigger;
      if (!collectionOk && !productOk) {
        return false;
      }
    }

    return true;
  }

  function giftLinesForConfig(cartItems, cfg) {
    var giftVid = String(cfg.variant_id);
    var configKey = gwpConfigKey(cfg);

    return cartItems.filter(function (item) {
      if (!item || String(item.variant_id) !== giftVid) {
        return false;
      }

      var props = item.properties || {};
      if (!isFreeProductProperty(props["_free-product"])) {
        return false;
      }

      var lineConfigId = props["_gwp_config_id"];
      if (lineConfigId == null || lineConfigId === "") {
        return true;
      }

      return String(lineConfigId) === configKey;
    });
  }

  /**
   * Lines to remove when a gift is no longer eligible. Prefers property-tagged
   * lines; if none are found, falls back to any line for the configured gift
   * variant that is priced at zero. The fallback matters for auto-added gifts
   * whose property tag went missing (e.g. Shopify merged the line, or the gift
   * was added by an earlier flow), so a stale free gift is still cleaned up.
   */
  function giftLinesForRemoval(cartItems, cfg) {
    var tagged = giftLinesForConfig(cartItems, cfg);
    if (tagged.length > 0) {
      return tagged;
    }

    var giftVid = String(cfg.variant_id);
    return cartItems.filter(function (item) {
      if (!item || String(item.variant_id) !== giftVid) {
        return false;
      }
      var props = item.properties || {};
      return isFreeProductProperty(props["_free-product"]) || lineSpendCents(item) === 0;
    });
  }

  function qualifyingQuantity(cartItems, giftVariantId, productIds) {
    return cartItems.reduce(function (sum, item) {
      if (!item || String(item.variant_id) === giftVariantId) {
        return sum;
      }
      if (!productIds[String(item.product_id)]) {
        return sum;
      }
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (res) {
      return res.json();
    });
  }

  function isErrorResponse(res) {
    return !!(res && (res.errors || res.status === 422 || res.message));
  }

  function changeLineQuantity(lineKey, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).then(function (res) {
      return res.json();
    });
  }

  function addGift(giftVid, configKey) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: Number(giftVid) || giftVid,
            quantity: GWP_MAX_LINE_QUANTITY,
            properties: {
              "_free-product": "1",
              "_gwp_config_id": configKey,
            },
          },
        ],
      }),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          return {
            errors: data.description || data.message || "Could not add free gift",
            status: res.status,
          };
        }
        return data;
      });
    });
  }

  function removeGiftLines(lines) {
    var failed = false;
    var lastCart = null;
    var chain = Promise.resolve();

    lines.forEach(function (line) {
      if (!line || !line.key) {
        return;
      }
      chain = chain.then(function () {
        if (failed) {
          return null;
        }
        return changeLineQuantity(line.key, 0).then(function (res) {
          if (isErrorResponse(res)) {
            failed = true;
            return null;
          }
          lastCart = res;
          return res;
        });
      });
    });

    return chain.then(function () {
      return failed ? null : lastCart;
    });
  }

  function giftVariantInCart(cartItems, cfg) {
    var giftVid = String(cfg.variant_id);
    return cartItems.some(function (item) {
      return item && String(item.variant_id) === giftVid;
    });
  }

  function selectApplicableConfig(cartItems, cartSpendCents, options) {
    var configs = getGwpConfigs();
    var forBanner = !!(options && options.forBanner);

    for (var i = 0; i < configs.length; i++) {
      var cfg = configs[i];

      if (forBanner && isHiddenOnCartDrawer(cfg)) {
        continue;
      }

      if (giftVariantInCart(cartItems, cfg)) {
        continue;
      }

      if (qualifiesForGift(cfg, cartItems, cartSpendCents)) {
        return cfg;
      }
    }

    return null;
  }

  function removeStaleGifts(cartItems, cartSpendCents) {
    var configs = getGwpConfigs();
    var staleLines = [];

    configs.forEach(function (cfg) {
      var giftLines = giftLinesForRemoval(cartItems, cfg);

      if (giftLines.length === 0) {
        return;
      }

      if (!qualifiesForGift(cfg, cartItems, cartSpendCents)) {
        staleLines = staleLines.concat(giftLines);
      }
    });

    if (staleLines.length === 0) {
      return Promise.resolve(false);
    }

    return removeGiftLines(staleLines).then(function (data) {
      return data != null;
    });
  }

  /**
   * Add any qualifying auto-add gifts that are not yet in the cart. Evaluated
   * against the current cart snapshot; gifts are excluded from the spend/quantity
   * gates so adding one gift does not affect another's qualification.
   * Resolves true when at least one gift was added.
   */
  function autoAddQualifyingGifts(cartItems, cartSpendCents) {
    var configs = getGwpConfigs();
    var toAdd = configs.filter(function (cfg) {
      return (
        isAutoAdd(cfg) &&
        !giftVariantInCart(cartItems, cfg) &&
        qualifiesForGift(cfg, cartItems, cartSpendCents)
      );
    });

    if (toAdd.length === 0) {
      return Promise.resolve(false);
    }

    var chain = Promise.resolve();
    var added = false;

    toAdd.forEach(function (cfg) {
      chain = chain.then(function () {
        return addGift(cfg.variant_id, gwpConfigKey(cfg)).then(function (res) {
          if (!isErrorResponse(res)) {
            added = true;
          }
        });
      });
    });

    return chain.then(function () {
      return added;
    });
  }

  function hideBanner(banner) {
    banner.hidden = true;
    banner.setAttribute("data-gwp-config-id", "");
    banner.setAttribute("data-gwp-variant-id", "");
  }

  function renderBanner(banner, cfg) {
    banner.setAttribute("data-gwp-config-id", gwpConfigKey(cfg));
    banner.setAttribute("data-gwp-variant-id", String(cfg.variant_id));

    var img = banner.querySelector(".free-product-gwp__image img");
    if (img) {
      if (cfg.image) {
        img.src = cfg.image;
        img.alt = cfg.title || "Free Gift!";
        img.style.display = "";
      } else {
        img.removeAttribute("src");
        img.style.display = "none";
      }
    }

    var title = banner.querySelector(".free-product-gwp__title");
    if (title) {
      title.textContent = cfg.title || "";
    }

    var description = banner.querySelector(".free-product-gwp__description");
    if (description) {
      description.innerHTML = cfg.description || "";
      description.style.display = cfg.description ? "" : "none";
    }

    // Auto-add gifts are added without user action, so hide the ATC button.
    var autoAdd = isAutoAdd(cfg);
    var atc = banner.querySelector(".free-product-gwp__atc");
    if (atc) {
      atc.style.display = autoAdd ? "none" : "";
    }
    var addButton = banner.querySelector("[data-cart-gwp-add]");
    if (addButton) {
      addButton.disabled = autoAdd;
    }

    banner.hidden = false;
  }

  function updateBanner(cartItems) {
    var banner = document.querySelector(BANNER_SELECTOR);
    if (!banner) {
      return;
    }

    var cartSpendCents = cartSpendExcludingGiftsCents(cartItems);
    var cfg = selectApplicableConfig(cartItems, cartSpendCents, { forBanner: true });
    if (!cfg) {
      hideBanner(banner);
      return;
    }

    renderBanner(banner, cfg);
  }

  /** Re-fetch cart HTML and update mini-cart items + footer (no navigation). */
  function refreshSideCartContents() {
    var inner = document.querySelector(".side-cart__inner");
    var footer = document.querySelector(".side-cart__footer");

    return fetch("/cart")
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var items = doc.querySelector(".main-cart-items-table");

        if (items && inner) {
          inner.innerHTML = items.outerHTML;
          inner.classList.remove("side-cart__inner--empty");

          var promo = doc.querySelector(".shipping__promo");
          if (promo) {
            inner.prepend(promo);
          }
        }

        var updatedFooter = doc.querySelector(".side-cart__footer");
        if (updatedFooter && footer) {
          footer.innerHTML = updatedFooter.innerHTML;
          footer.classList.add("side-cart__footer--visible");
        }
      })
      .then(function () {
        if (window.Douglas && typeof window.Douglas.setSideCartItemsMaxHeight === "function") {
          window.Douglas.setSideCartItemsMaxHeight();
        }

        if (window.Douglas && typeof window.Douglas.updateFreeCartItemQuantityVisibility === "function") {
          window.Douglas.updateFreeCartItemQuantityVisibility();
        }
      });
  }

  function refreshCartUI(options) {
    var opts = options || {};
    var onCartPage = window.location.pathname.indexOf("/cart") > -1;

    if (onCartPage && opts.reloadCartPage) {
      window.location.reload();
      return Promise.resolve();
    }

    return refreshSideCartContents().then(function () {
      document.body.dispatchEvent(new Event("calculate__promo"));
    });
  }

  var debounceId = null;
  var running = false;
  var needsFollowUpSync = false;

  function scheduleSync() {
    if (debounceId) {
      clearTimeout(debounceId);
    }
    debounceId = setTimeout(function () {
      debounceId = null;
      runSync();
    }, SYNC_DEBOUNCE_MS);
  }

  function runSync() {
    if (getGwpConfigs().length === 0) {
      return;
    }

    if (running) {
      needsFollowUpSync = true;
      return;
    }
    running = true;
    needsFollowUpSync = false;

    var changedByRemoval = false;
    var changedByAutoAdd = false;

    getCart()
      .then(function (cartData) {
        if (!cartData || !Array.isArray(cartData.items)) {
          return null;
        }
        var cartSpendCents = cartSpendExcludingGiftsCents(cartData.items);
        return removeStaleGifts(cartData.items, cartSpendCents).then(function (removed) {
          changedByRemoval = removed;
          if (removed) {
            return getCart();
          }
          return cartData;
        });
      })
      .then(function (cartData) {
        if (!cartData || !Array.isArray(cartData.items)) {
          return cartData;
        }
        var cartSpendCents = cartSpendExcludingGiftsCents(cartData.items);
        return autoAddQualifyingGifts(cartData.items, cartSpendCents).then(function (added) {
          changedByAutoAdd = added;
          if (added) {
            return getCart();
          }
          return cartData;
        });
      })
      .then(function (cartData) {
        if (cartData && Array.isArray(cartData.items)) {
          updateBanner(cartData.items);
        }
      })
      .catch(function () {
        /* leave cart untouched on failure */
      })
      .then(function () {
        running = false;
        if (changedByRemoval || changedByAutoAdd) {
          refreshCartUI({ reloadCartPage: true });
        }
        if (needsFollowUpSync) {
          needsFollowUpSync = false;
          scheduleSync();
        }
      });
  }

  function handleBannerAddClick(event) {
    var button = event.target.closest("[data-cart-gwp-add]");
    if (!button) {
      return;
    }

    var banner = button.closest(BANNER_SELECTOR);
    if (!banner) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var variantId = banner.getAttribute("data-gwp-variant-id");
    var configKey = banner.getAttribute("data-gwp-config-id") || "";
    if (!variantId) {
      return;
    }

    if (button.disabled || button.getAttribute("aria-busy") === "true") {
      return;
    }

    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    addGift(variantId, configKey)
      .then(function (res) {
        if (isErrorResponse(res)) {
          button.disabled = false;
          button.removeAttribute("aria-busy");
          return;
        }

        hideBanner(banner);

        return getCart()
          .then(function (cartData) {
            if (cartData && Array.isArray(cartData.items)) {
              updateBanner(cartData.items);
            }
            return refreshCartUI();
          })
          .then(function () {
            document.body.dispatchEvent(new Event("cart__updated"));
          });
      })
      .catch(function () {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      })
      .then(function () {
        if (button.isConnected) {
          button.disabled = false;
          button.removeAttribute("aria-busy");
        }
      });
  }

  function bindCartListeners() {
    if (document.body.getAttribute("data-gwp-bound") === "true") {
      return;
    }
    document.body.setAttribute("data-gwp-bound", "true");

    document.body.addEventListener("cart__updated", scheduleSync);
    document.body.addEventListener("calculate__promo", scheduleSync);
    document.body.addEventListener("click", handleBannerAddClick, true);

    document.querySelectorAll("cart-items").forEach(function (el) {
      el.addEventListener("change", scheduleSync);
    });

    document.addEventListener("shopify:section:load", scheduleSync);
  }

  function init() {
    bindCartListeners();

    if (getGwpConfigs().length > 0) {
      runSync();
    }
  }

  if (document.readyState !== "loading") {
    init();
  } else {
    document.addEventListener("DOMContentLoaded", init);
  }
})();
